import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Stack,
    Typography,
    SxProps,
    Theme,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { GREEN_800, GREEN_900, iconCircleSx, sectionCardSx } from './pvtaStyles';

interface SectionCardProps {
    title: string;
    icon?: React.ReactNode;
    /** Optionale führende Nummer (z.B. "1. Persönliche Daten"). */
    number?: number;
    /** true => einklappbare Accordion-Card, false => statische Card. */
    collapsible?: boolean;
    defaultExpanded?: boolean;
    /** Optionale Aktion rechts im Header (nur statische Variante). */
    action?: React.ReactNode;
    sx?: SxProps<Theme>;
    children: React.ReactNode;
}

const HeaderTitle: React.FC<{ number?: number; title: string }> = ({ number, title }) => (
    <Typography sx={{ fontWeight: 700, color: GREEN_900, fontSize: 16 }}>
        {number !== undefined ? `${number}. ${title}` : title}
    </Typography>
);

/**
 * Einheitliche PVTA-Section-Card. Ersetzt die zuvor pro Seite inline
 * definierten Varianten (AccountEdit = Accordion, ReservationEdit = Box).
 */
const SectionCard: React.FC<SectionCardProps> = ({
    title,
    icon,
    number,
    collapsible = false,
    defaultExpanded = true,
    action,
    sx,
    children,
}) => {
    if (collapsible) {
        return (
            <Accordion
                defaultExpanded={defaultExpanded}
                disableGutters
                elevation={0}
                sx={[sectionCardSx, ...(Array.isArray(sx) ? sx : [sx])]}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreRoundedIcon sx={{ color: GREEN_800 }} />}
                    sx={{
                        px: { xs: 2, md: 2.5 },
                        py: 1,
                        minHeight: 60,
                        '& .MuiAccordionSummary-content': { my: 1.25, alignItems: 'center' },
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        {icon && <Box sx={iconCircleSx}>{icon}</Box>}
                        <HeaderTitle number={number} title={title} />
                    </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pt: 0.5, pb: 2.5 }}>
                    {children}
                </AccordionDetails>
            </Accordion>
        );
    }

    return (
        <Box sx={[sectionCardSx, { p: { xs: 2, md: 2.5 } }, ...(Array.isArray(sx) ? sx : [sx])]}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1.5}
                sx={{ mb: 2 }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                    {icon && <Box sx={iconCircleSx}>{icon}</Box>}
                    <HeaderTitle number={number} title={title} />
                </Stack>
                {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
            </Stack>
            {children}
        </Box>
    );
};

export default SectionCard;
